import { common, shopify, shopline } from '@/constant/company';

/**
 * 弹窗触发类型（与后端/配置一致，新人可查此表）
 * 配置/接口里的 type 数字含义见下，业务里判断时可用 POPUP_TRIGGER_TYPE.xxx 替代魔法数字
 */
export const POPUP_TRIGGER_TYPE = {
  /** 0: 页面进入/延迟 */
  PAGE_ENTER: 0,
  /** 1: 离开意图 */
  LEAVE_INTENT: 1,
  /** 2: 加购 */
  ADD_CART: 2,
  /** 3: 倒计时 */
  COUNTDOWN: 3,
  /** 4: 老用户 */
  OLD_USER: 4,
  /** 5: 下滑 */
  SCROLL: 5,
  /** 6: 点击 */
  CLICK: 6,
  /** 8: 点击（另一枚举值） */
  CLICK_8: 8,
  /** 10: 新客订阅 */
  NEW_USER_SUBSCRIBE: 10
} as const;

/** 弹窗 step：曝光上报用，封面=-1，主内容=1 */
export const POPUP_STEP = {
  COVER: -1,
  MAIN: 1
} as const;

export const CARTSEE_TARGET_REDIRECT = 'cartsee_target=_redirect';

export const CARTSEE_CART_TIME_INTERVAL = 60 * 1000; // 1min
export const CARTSEE_CART_TIME_KEY = '_cartsee_popup_cart_time';

/**
 * 自定义事件名称
 */
export const EVENTS = {
  // 弹窗配置
  POPUP: {
    STORED: 'cartsee:popup:stored', // 配置存储
    REMOVED: 'cartsee:popup:removed', // 配置移除
    SIGNUP: 'cartsee:popup:signup', // 注册
    COUPON: 'cartsee:popup:coupon' // 优惠券
  }
};

/**
 * 配置移除的原因
 */
export const REMOVE_TYPE = {
  EXPIRED: 'expired', // 配置过期
  COUPON_MISMATCH: 'coupon_mismatch' // 优惠券不匹配
};

/**
 * 存储的 key（localStorage / sessionStorage 统一在此维护，易扩展）
 * 使用 utils/storage 的 getLocal/getSession/setLocal/setSession/removeLocal/removeSession 读写
 * 结构打平，按类别注释归类便于维护
 */
export const STORAGE_KEY = {
  // --- 用户/表单（local）---
  CID: 'cid',
  FORM_UID: 'formUid',
  CARTX_CUSTOMER: 'cartx-customer',
  CARTSEE_ID: '_cartsee_id',
  CORRELATION_ID: 'correlationId',

  // --- 统计（local）---
  CARTSEE_STAT: '_cartsee_stat',

  // --- 实验/配置（local）---
  AB_MAP: '_cartsee_map',

  // --- 弹窗（session 除 TEMP_POP / STEP / SHOW_COVER 为 local）---
  POPUP: '_cartsee_popup_config',
  TEMP_POP: '_cartsee_tempPop',
  POP_SHOW: '_cartsee_pop_show',
  SHOW_FROM: '_cartsee_show_from',
  LEAVE_TRIGGER_TYPE: 'leaveTriggerType',
  /** 新用户订阅 leaveBeta（type=0）最近一次成功展示时间戳（session），用于按 displayFrequency 做冷却 */
  LEAVE_BETA_TYPE0_LAST_SHOWN_AT: 'leaveBetaType0LastShownAt',
  SOLD_OUT_POPUP_CONFIG: '_cartsee_sold_out_popup_config',
  /** 弹窗步骤（local），实际 key = CARTSEE_STEP_PREFIX + 弹窗 id，分步展示跨页恢复 */
  CARTSEE_STEP_PREFIX: '_cartsee_step_',
  /** 弹窗封面是否显示（local），实际 key = CARTSEE_SHOW_COVER_PREFIX + 弹窗 id */
  CARTSEE_SHOW_COVER_PREFIX: '_cartsee_showCover_',
  /** 当前会话已弹弹窗数量（session） */
  ALL_POP_COUNT: 'allPopCount',
  /** 当前会话已弹退出挽留弹窗数量（session） */
  LEAVE_POP_COUNT: 'leavePopCount',

  // --- 优惠券 ---
  COUPON: '_cartsee_coupon_store',
  COUPON_STORE_SESSION: '_cartsee_coupon',

  // --- 加购/流程（session）---
  ADD_CART_STATE: '_cartsee_state',
  ADD_CART_ITEMS: '_cartsee_items',
  ADD_CART_JUMP: '_cartsee_add_cart_jump',
  CART_LAST_IMAGE: '_cartsee_cart_last_image',
  ADD_CART_URL: '_cartsee_add_cart_url',
  EXCLUDE_PRODUCT_IDS: 'excludeProductIds',
  EXCLUDE_PRODUCT_IDS_VERSION: 'excludeProductIdsVersion',

  // --- 其他 ---
  LOAD_ERROR: 'load-error',
  VIEW_ERROR: 'view-error',
  GIVE_UP_ORDER: 'giveUpOrder',
  COMPANY_STATIC_URL: 'companyStaticUrl',
  LOAD_JS_TIME: '__cartsee_load_script_time__',
  NEWSLETTER_EMAIL: '_cartsee_newsletter_email',
  ONE_ID: '_cartsee_oneid',
  OLD_EMAIL: '_cartx_old_email',
  CARTX_BANNER: 'cartx-banner',
  CARTX_FLOAT_BANNER: 'cartx-float-bar-wrapper',
  CARTX_FLOAT_BANNER_DATA: 'cartx-float-bar-wrapper-data',
  /** 接口下发的国家码（session） */
  IP_COUNTRY_CODE: 'ipCountryCode',
  /** 调试：vconsole 开关，'1' 表示已开启（local） */
  VCONSOLE: '_vconsole',
  /** 加购 sku 列表缓存（local），部分渠道购物车恢复用 */
  CARX_GET_CART_SKUS: 'carxGetCartSkus',
  /** 从 URL 写入的 cid（local），与 CID 区分、部分场景用 */
  CARTX_CID: 'cartx_cid',
  /** 调试：加购/竹乐等 debug 开关（local） */
  CARTSEE_DEBUG: '_cartsee_debug',
  /** 调试：catchLog 等通用 debug 开关，值含 cartsee: 时开启（local） */
  DEBUG: 'debug',
  /** stream 订阅流程上报用（local） */
  STREAM_SUBSCRIBE_PROCESS: 'stream_subscribe_process',
  STREAM_SUBSCRIBE_RENDERED: 'stream_subscribe_rendered',
  STREAM_SUBSCRIBE_COLLECTED: 'stream_subscribe_collected',
  /** 表单/468 等使用的邮箱（local） */
  CARTX_EMAIL: 'cartx_email',
  /** 加购弹窗冷却时间戳（local） */
  CARTSEE_POPUP_CART_TIME: '_cartsee_popup_cart_time',
  /** 灰度店铺 load 接口上次成功请求时间（session） */
  POPUP_LOAD_REQUEST_TIME: '_cartsee_popup_config_request_time',
  /** 退出意图弹窗上次检查时间（session），后缀 _1 表示 type=1 */
  POPUP_LAST_CHECK_TIME_1: '_cartsee_popup_lastCheckTime_1',
  /** 468 埋点等使用的表单缓存（local） */
  FORM_STORAGE_ITEM: 'FORM_STORAGE_ITEM',
  /** 468 埋点等使用的旧邮箱缓存（local） */
  OLD_EMAIL_LEGACY: 'old-email',
  /** 468 埋点等使用的旧手机号缓存（local） */
  OLD_PHONE_LEGACY: 'old-phone',
  /** GA 监听：最后活跃时间（local） */
  LAST_ACTIVE_TIME: '__last_active_time',
  /** GA 监听：cartsee 到站时间（local） */
  LAST_CACTSEE_ACTIVE_TIME: '__last_cartsee_active_time',
  /** GA 监听：cartsee 到站 GA（local） */
  LAST_CACTSEE_ACTIVE_GA: '__last_cartsee_active_ga',
  /** GA 监听：最后活跃信息（local） */
  LAST_ACTIVE_INFO: '__last_active_info',
  /** GA 监听：utm 来源列表（local） */
  UTM_SOURCE_LIST: '__utm_source_list',
  /** 调试：cartsee debug 输出开关（local） */
  CS_DEBUG: '__cs_debug',
  /** 页面加载时间戳（session），用于监控 */
  PAGE_LOAD: 'cartx_page_load'
}

/**
 * 表单邮箱
 */
export const PREV_FORM_EMAILS = '__cs_email';

/**
 * 表单手机号
 */
export const PREV_FORM_PHONES = '__cs_phone';

/**
 * 竹乐公司
 */
export const AOOLIA = [common[1299], common[1536], common[1545], common[1546], common[2107], common[2109], common[2110]];

/**
 * 竹乐自建站
 */
export const DRAW = [common[2016], common[2306], common[2457], common[2458]];

/**
 * 竹乐heyprom
 */
export const HEYPROM = [common[2183], common[2362], common[2363], common[2364], common[2365], common[2366]];

/**
 * 竹乐missacc
 */
export const MISSACC = [common[899], common[1182], common[1804], common[1881], common[1882], common[1883]];

/**
 * 竹乐helloice
 */
export const HELLOICE = [common[1334], common[1527], common[1997]];

/**
 * 乐其smallrig
 */
export const SMALLRIG = ['m9s50k1d', 'maysianp', 'maytqbi7', 'maytr53f', 'maytsl4f', 'maytt4uv', 'mayv0zyg', 'maz2fvbw', 'maz460om', 'mb0g47i3'];

/**
 * POVISON
 */
export const POVISON = [common[1892], common[2042], common[2044], common[2058], common[2059], common[2075], common[2076], common[2131], common[2133]];

/**
 * 必填功能灰度公司
 */
export const NECESSARY_COMPANY = ['l2y7m6c5', 'lhe9xn2g', 'leqwd9rx', 'lgotkqvw', 'lhk7nlb1', 'lkry5o69', 'lnsjqpgt', 'lo2mj5ww', 'lt6qceic', 'lt80etle', 'lv4vaq57', 'lwiou1mv'];

/**
 * 澳维
 */
export const AOWEI = [
  shopify[724],
  shopline[689],
  shopline[895],
  shopline[838],
  shopline[1661],
  shopline[1451],
  shopline[1459],
  shopify[1510],
  shopline[1535],
  shopify[1598],
  shopline[1657],
  shopline[884],
  shopify[1034], // 老店铺
  shopline[2273], // 新店铺
  shopify[1378],
  shopline[2096],
  shopline[1739],
  shopline[1737],
  shopify[2095],
  shopify[1682],
  shopline[2641],
  shopline[2123],
  shopify[2377],
  shopline[2691],
  shopline[2452],
  shopline[2783],
  shopline[2244],
  shopify[2152]
];

export const AUTH_CHANNEL = {
  FACEBOOK: 1,
  GOOGLE: 2,
  APPLE: 3
};
